import { useTranslation } from "react-i18next";
import type { Document } from "types";
import CrudEdit from "common/components/Crud/CrudEdit";
import { TableColumn } from "common/components/DataTable/DataTable";
import { CrudHeaderAction } from "common/components/Crud/components/CrudHeader";
import CrudList from "common/components/Crud/CrudList";
import { useMemo } from "react";
import useRouter from "common/hooks/use-router";
import { CrudFilter } from "common/components/Crud/components/CrudFilters";
import ProductSelect from "components/select/ProductSelect/ProductSelect";
import TagSelect from "components/select/TagSelect/TagSelect";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import { ProductNameLabel } from "pages/Products/productUtils";
import DocumentForm from "./DocumentForm";

function ProductFilterSelect({ value, onChange, ...props }: any) {
  const selectValue = value?.id?.$eq ?? null;
  return (
    <ProductSelect
      value={selectValue}
      onChange={(id: number) =>
        onChange(id != null ? { id: { $eq: id } } : undefined)
      }
      {...props}
    />
  );
}

function TagFilterSelect({ value, onChange, ...props }: any) {
  const selectValue = value?.id?.$eq ?? null;
  return (
    <TagSelect
      value={selectValue}
      onChange={(id: number) =>
        onChange(id != null ? { id: { $eq: id } } : undefined)
      }
      {...props}
    />
  );
}

function SidebarDocumentForm({ ...props }: any) {
  return <CrudEdit collectionName="documents" Form={DocumentForm} {...props} />;
}

function Documents() {
  const { t } = useTranslation();
  const { navigate } = useRouter();

  const handleNavigate = (id: any) => {
    navigate(`/documents/${id}`);
  };

  const columns: TableColumn<Document>[] = useMemo(
    () => [
      {
        field: "media",
        header: t("documents.media"),
        renderType: "file",
      },
      {
        field: "products",
        header: t("documents.products"),
        customRender: (rowData) => {
          const products = rowData.products ?? [];
          if (!products.length) return <span>-</span>;
          const visible = products.slice(0, 3);
          const rest = products.slice(3);
          return (
            <div className="flex flex-col gap-1">
              {visible.map((p: any) => (
                <div key={p.id} className="flex items-center gap-1.5">
                  {p.image?.url ? (
                    <img
                      src={ImageFromStrapiMedia(p.image).uri}
                      alt={p.name}
                      className="w-5 h-5 rounded object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded bg-slate-200 shrink-0" />
                  )}
                  <ProductNameLabel
                    product={p}
                    className="text-xs text-slate-600"
                    showColor={false}
                  />
                </div>
              ))}
              {rest.length > 0 && (
                <TooltipWrapper
                  content={
                    <div className="flex flex-col gap-0.5">
                      {rest.map((p: any) => (
                        <ProductNameLabel key={p.id} product={p} />
                      ))}
                    </div>
                  }
                >
                  <span className="text-xs text-slate-400 cursor-default">
                    +{rest.length} {t("common.more")}
                  </span>
                </TooltipWrapper>
              )}
            </div>
          );
        },
      },
      {
        field: "tags",
        header: t("documents.tags"),
        customRender: (rowData) => {
          const tags = rowData.tags || [];
          if (!tags.length) return <span>-</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {tags.map((tag: any) => (
                <span
                  key={tag.id}
                  className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600 border border-slate-200"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        field: "isSuggested",
        header: t("documents.isSuggestedShort"),
        renderType: "boolean",
      },
    ],
    [t],
  );

  const actions: CrudHeaderAction[] = useMemo(
    () => [
      {
        title: t("common.new"),
        icon: "PlusCircleIconSolid",
        action: "openSidebar",
        type: "white",
        className: "flex flex-row-reverse",
      },
    ],
    [t],
  );

  const filters: CrudFilter[] = useMemo(() => {
    return [
      {
        name: "name$containsi",
        label: t("documents.media"),
        renderType: "input",
        icon: "MagnifyingGlassIcon",
      },
      {
        name: "products",
        label: t("documents.products"),
        renderType: "custom",
        renderComponent: (
          <ProductFilterSelect placeholder={t("documents.products")} />
        ),
      },
      {
        name: "tags",
        label: t("documents.tags"),
        renderType: "custom",
        renderComponent: <TagFilterSelect placeholder={t("documents.tags")} />,
      },
    ];
  }, [t]);

  return (
    <CrudList
      collectionName="documents"
      columns={columns}
      headerActions={actions}
      filters={filters}
      canDelete
      canEdit
      formMode="sidebar"
      SidebarFormComponent={SidebarDocumentForm}
      onNavigate={handleNavigate}
      actionsHeader=""
      className="pt-2 px-4"
    />
  );
}

export default Documents;
